<script setup lang="ts">
import { NxtModal, NxtButton, NxtIconButton } from '@future-harry/nxt-styles';
import { MediaItem } from '@/modules/media-library/components/media-library.interface';
import MediaLibrary from '@/modules/media-library/components/MediaLibrary.vue';

const selectedMediaUrl = defineModel<string | null>('modelValue');
const selectedMediaId = defineModel<number | null>('mediaId');

const props = withDefaults(
   defineProps<{
      id?: number;
      type?: 'image' | 'video';
      error?: string | null;
      showImages?: boolean;
      showVideos?: boolean;
   }>(),
   {
      type: 'image',
      showImages: true,
      showVideos: false,
   },
);

const pageId = inject<string>('pageId') as string;
const jobBoardId = computed(() => props.id ?? parseInt(pageId));

const showModal = ref(false);

function handleSelectMedia(media: MediaItem | null) {
   selectedMediaUrl.value = media?.url ?? null;
   selectedMediaId.value = media?.id ?? null;
}

function deleteSelected() {
   selectedMediaUrl.value = null;
   selectedMediaId.value = null;
}

function selectMedia() {
   showModal.value = false;
}

function cancel() {
   selectedMediaUrl.value = null;
   selectedMediaId.value = null;
   showModal.value = false;
}
</script>

<template>
   <section class="media-selector" :class="$attrs.class">
      <template v-if="!selectedMediaUrl">
         <NxtButton :button-type="'grey'" @click="showModal = true">{{ $t('actions.select') }}</NxtButton>
      </template>
      <template v-else>
         <div class="delete-action">
            <NxtIconButton
               :name="'trash'"
               :color="'var(--nxt-white)'"
               :background-color="'var(--nxt-dark)'"
               :hover-color="'var(--nxt-medium-dark)'"
               @on-click="deleteSelected"
            />
         </div>
         <img v-if="type.includes('image')" :src="selectedMediaUrl" />
         <video v-else-if="type.includes('video')" :src="selectedMediaUrl" controls></video>
      </template>

      <NxtModal
         v-if="showModal"
         width="84.6rem"
         name="media-library"
         :title="$t('media-library.title')"
         :primary-action-text="$t('actions.select')"
         :cancel-text="$t('actions.cancel')"
         @on-primary-action="selectMedia"
         @on-cancel="cancel"
      >
         <MediaLibrary @on-select-media="handleSelectMedia" :jobBoardId="jobBoardId" :show-images="showImages" :show-videos="showVideos" />
      </NxtModal>
   </section>
   <div v-if="props.error" class="error-message">
      <small>{{ props.error }}</small>
   </div>
</template>

<style lang="scss" scoped>
.error-message {
   color: var(--nxt-red);
}

.media-selector {
   aspect-ratio: 3 / 2;
   border-radius: var(--nxt-radius);
   display: flex;
   align-items: center;
   justify-content: center;
   background: var(--nxt-grey);
   position: relative;
   overflow: hidden;

   video {
      width: 100%;
      height: 100%;
   }

   img {
      max-width: 100%;
      max-height: 100%;
      object-fit: contain;
   }
}

.selected-media {
   max-width: 100%;
   max-height: 100%;
   object-fit: cover;
   display: block;
}

.delete-action {
   position: absolute;
   top: 0;
   right: 0;
   z-index: 1;
}
</style>
